import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { useOutreachPlan } from '../hooks/useOutreachPlan';
import { useUpgradePrompt } from '../../hooks/useUpgradePrompt';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { TargetCover } from '../find/TargetCover';
import { TARGET_KIND_LABEL } from '../outreachLabels';
import { compactCount } from '../outreachFormat';
import type { OutreachLiveSearch } from '../outreachTypes';
import { knownKind } from './SearchMatchRow';
import { stateMessages as messages } from './stateMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the live screens; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Live.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface FreePreviewStateProps {
  song: string;
  catalogItemUuid: string;
  search: OutreachLiveSearch;
}

/**
 * Free plan and trial preview (design t6): the real match count and the two best real matches.
 * Pitching starts on a paid plan: "See plans" opens the plan offer (`useOutreachPlan`).
 */
const FreePreviewStateComponent: React.FC<FreePreviewStateProps> = ({ song, catalogItemUuid, search }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  const plan = useOutreachPlan();
  const openUpgradePrompt = useUpgradePrompt();
  const top = useMemo(() => search.matches.slice(0, 2), [search.matches]);
  const rows = useMemo(
    () =>
      top.map((match, index) => (
        <li key={match.targetUuid} className={clsx(styles.match, index > 0 && styles.dim)}>
          <TargetCover imageUrl={match.coverUrl} kind={knownKind(match.kind)} className={styles.smallCover} />
          <div className={styles.matchText}>
            <div className={styles.matchName}>{match.name}</div>
            <div className={styles.matchSub}>
              {intl.formatMessage(TARGET_KIND_LABEL[knownKind(match.kind)])}
              {match.followers === null ? '' : ` · ${compactCount(intl, match.followers)}`}
            </div>
          </div>
          <span className={clsx(shared.chip, shared.chipPos)}>
            {intl.formatMessage(messages.fit, { score: match.fit })}
          </span>
        </li>
      )),
    [intl, top]
  );
  return (
    <section className={clsx(styles.card, styles.state)} data-testid="outreach-state-free">
      <div className={shared.row}>
        <span className={shared.eyebrow}>{intl.formatMessage(messages.freeEyebrow)}</span>
        <span className={`${shared.chip} ms-auto`}>{intl.formatMessage(messages.freePreview)}</span>
      </div>
      <h2 className={styles.stateTitle}>
        {intl.formatMessage(messages.freeTitle, { count: search.matchCount, song })}
      </h2>
      <ul className={`${styles.matches} p-0 m-0`}>{rows}</ul>
      <p className={styles.stateBody}>{intl.formatMessage(messages.freeBody)}</p>
      <div className={styles.stateActions}>
        <SMLButton outline omitCap onClick={() => router.push(SMLRoutes.outreachFind, { song: catalogItemUuid })}>
          {intl.formatMessage(messages.seeAll, { count: intl.formatNumber(search.matchCount) })}
        </SMLButton>
        <SMLButton
          color="primary"
          omitCap
          onClick={() => openUpgradePrompt(SMLUpgradeFeature.outreach, song)}
          disabled={plan.isDemo}
        >
          {intl.formatMessage(messages.seePlans)}
        </SMLButton>
      </div>
    </section>
  );
};

export const FreePreviewState = TypedMemo(FreePreviewStateComponent);
